import React, { useCallback, useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import { useSearchParams } from "react-router-dom";

import { fetchContractList } from "./api";
import ContractJournalBody from "./ContractJournalBody";
import ContactJournalHead from "./ContactJournalHead";
import ContractJournalHeader from "./ContractJournalHeader";
import EmptyOrShow from "../../components/common/EmptyOrShow";
import { PageTitle } from "../../components/common/pageTitle";
import { useCustomSnackbar } from "../../utils/hooks/useCustomSnackbar";

export const TableContext = React.createContext();

export const TableProvider = ({ children }) => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const value = [data, setData, searchTerm, setSearchTerm];
  return (
    <TableContext.Provider value={value}>{children}</TableContext.Provider>
  );
};

const FILTER_KEYS = [
  "remont_id",
  "contractor_id",
  "contractor_agreement_list_status_id"
];

function parsePositiveInt(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : "";
}

function filtersFromSearchParams(searchParams) {
  return {
    remont_id: parsePositiveInt(searchParams.get("remont_id")),
    contractor_id: parsePositiveInt(searchParams.get("contractor_id")),
    contractor_agreement_list_status_id: parsePositiveInt(
      searchParams.get("contractor_agreement_list_status_id")
    )
  };
}

function filtersToSearchParamsObject(filters) {
  const o = {};
  for (const key of FILTER_KEYS) {
    const val = filters[key];
    if (val !== "" && val != null) {
      o[key] = String(val);
    }
  }
  return o;
}

function hasActiveListFilters(f) {
  return !!(
    f.remont_id ||
    f.contractor_id ||
    f.contractor_agreement_list_status_id
  );
}

const headerTitles = [
  "ID документа",
  "ID Ремонта",
  "ЖК",
  "Квартира",
  "Наименование документа",
  "Сумма",
  "Инициатор",
  "Контрагент",
  "Статус",
  "Документы"
];
const useStyles = makeStyles({
  body: {
    padding: 20
  },
  wrapper: {
    overflow: "auto"
  }
});
export const ContractJournal = () => {
  const classes = useStyles();
  const { addCloseSnack } = useCustomSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [filters, setFilters] = useState(() =>
    filtersFromSearchParams(new URLSearchParams(window.location.search))
  );
  const [savedFilters, setSavedFilters] = useState(() =>
    filtersFromSearchParams(new URLSearchParams(window.location.search))
  );
  const [loading, setLoading] = useState(false);

  const getContractorList = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const res = await fetchContractList(params);
        setLoading(false);
        setData(res.data);
      } catch (e) {
        setLoading(false);
        const message = e?.response?.data?.error;
        message && addCloseSnack(message);
      }
    },
    [addCloseSnack]
  );

  const searchKey = searchParams.toString();

  useEffect(() => {
    const fromUrl = filtersFromSearchParams(new URLSearchParams(searchKey));

    setFilters(fromUrl);
    setSavedFilters(fromUrl);

    const run = async () => {
      if (hasActiveListFilters(fromUrl)) {
        await getContractorList(fromUrl);
      } else {
        await getContractorList();
      }
    };
    run();
  }, [searchKey, getContractorList]);

  const onFiltersChange = e => {
    setFilters(prev => ({
      ...prev,
      [e?.target.name]: Number(e?.target?.value) || ""
    }));
  };

  const getDataByParams = useCallback(() => {
    setSearchParams(filtersToSearchParamsObject(filters), { replace: true });
  }, [filters, setSearchParams]);

  return (
    <>
      <PageTitle title={"Журнал договоров"} />
      <Box className={classes.body}>
        <TableProvider>
          <ContractJournalHeader
            getDataByParams={getDataByParams}
            filters={filters}
            onFiltersChange={onFiltersChange}
            loading={loading}
          />
          <EmptyOrShow loading={loading}>
            <div className={classes.wrapper}>
              <ContactJournalHead headerTitles={headerTitles} />
              <ContractJournalBody
                filters={savedFilters}
                data={data}
                setData={setData}
              />
            </div>
          </EmptyOrShow>
        </TableProvider>
      </Box>
    </>
  );
};
